import { Box, Button, Stack, StackProps, styled, useTheme } from '@mui/material';
import { LabelAssignment } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { FORM_LABEL_WIDTH, FormLabel, OverflowList, Tooltip } from 'src/components';
import { NON_LABEL_GROUP_ID } from 'src/constants/label';
import { useLabelAssignment } from 'src/graphql';
import { useNotifications } from 'src/utils/hooks';
import { pickPastelColor } from 'src/utils/strings';
import { Label } from './label-component';
import { LabelForm } from './label-form';

export const CONTAINER_CLASS = 'label-popper-container';

export const MAX_LABELS_TO_SHOW = 5;

const LABEL_HEIGHT = '1.25rem';

const ActionButton = styled(Button)(() => ({
  fontSize: '0.6rem',
  width: 'fit-content',
  height: LABEL_HEIGHT,
  lineHeight: LABEL_HEIGHT,
  minWidth: 0,
}));

export const LabelField = ({
  labelAssignments,
  entityName,
  entityId,
  partnerId,
  isEditable = true,
  canAccessToPrivateLabels,
  ...props
}: StackProps & {
  isEditable?: boolean;
  entityId: string;
  entityName: string;
  partnerId?: number;
  canAccessToPrivateLabels?: boolean;
  labelAssignments: LabelAssignment[];
}) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const { showSuccessAlert } = useNotifications();

  const { deleteLabelAssignment } = useLabelAssignment();

  const onDelete = useCallback(
    async (label: LabelAssignment) => {
      if (!label) {
        return;
      }

      const deletedLabelAssignment = await deleteLabelAssignment({
        entity: entityName,
        entityId,
        labelIds: label.labelId,
        partnerId: label.partnerId ?? partnerId,
        canAccessToPrivateLabels,
      });

      if (deletedLabelAssignment) {
        showSuccessAlert('Label was removed');
      }
    },
    [
      canAccessToPrivateLabels,
      deleteLabelAssignment,
      entityId,
      entityName,
      partnerId,
      showSuccessAlert,
    ],
  );

  const renderLabel = useCallback(
    (labelAssignment: LabelAssignment) => {
      return (
        <Label
          isPrivate={!labelAssignment.label?.isPublic}
          key={labelAssignment.labelAssignmentId}
          label={labelAssignment.label?.displayName}
          size="small"
          backgroundColor={pickPastelColor(
            labelAssignment.label?.labelGroupId ?? NON_LABEL_GROUP_ID,
            theme.palette.mode,
          )}
          onDelete={
            isEditable
              ? async () => {
                  await onDelete(labelAssignment);
                }
              : undefined
          }
          sx={{
            '.MuiChip-deleteIcon': {
              fontSize: '0.95rem',
              width: '1.25rem',
            },
            '.MuiChip-icon': {
              fontSize: '1rem',
              marginRight: '-0.625rem',
            },
            height: LABEL_HEIGHT,
            width: 'auto',
          }}
        />
      );
    },
    [isEditable, onDelete, theme.palette.mode],
  );

  const renderShowMoreElement = useCallback(
    (hiddenCount: number) => {
      const hiddenLabels = labelAssignments
        .slice(-hiddenCount)
        .map((it) => it.label?.displayName)
        .join(`\n`);
      return (
        <Tooltip
          title={hiddenLabels}
          slotProps={{ popper: { sx: { whiteSpace: 'pre-wrap' } } }}
        >
          <ActionButton size="small" onClick={() => setIsOpen(true)}>
            +{hiddenCount}
          </ActionButton>
        </Tooltip>
      );
    },
    [labelAssignments],
  );

  const showLessElement = useMemo(() => {
    return (
      <Box>
        <ActionButton size="small" onClick={() => setIsOpen(false)}>
          Show Less
        </ActionButton>
      </Box>
    );
  }, []);

  return (
    <Stack {...props} direction="row" minWidth={0}>
      <Stack
        flexWrap="wrap"
        alignItems={labelAssignments.length ? 'flex-start' : 'center'}
        direction={labelAssignments.length ? 'column' : 'row'}
        gap={1}
        flexGrow={1}
        minWidth={0}
      >
        <OverflowList
          key={labelAssignments.length}
          sx={{
            gap: '0.3rem',
            flexWrap: isOpen ? 'wrap' : 'nowrap',
            minWidth: FORM_LABEL_WIDTH,
            maxWidth: '100%',
            overflow: 'hidden',
            ...(labelAssignments.length && { alignSelf: 'stretch' }),
          }}
          items={labelAssignments}
          itemRenderer={renderLabel}
          showMoreElement={renderShowMoreElement}
          showAll={isOpen}
          startElement={<FormLabel label="Labels" />}
          endElement={isOpen && !!labelAssignments.length ? showLessElement : null}
        />
        {!!isEditable && (
          <LabelForm
            entityId={entityId}
            entityName={entityName}
            labelAssignments={labelAssignments}
            partnerId={partnerId}
            renderTrigger={({ getTriggerProps }) => (
              <ActionButton
                {...getTriggerProps()}
                size="small"
                sx={{
                  ...(!!labelAssignments.length &&
                    !isOpen && { marginLeft: FORM_LABEL_WIDTH }),
                }}
              >
                + Label
              </ActionButton>
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};
